import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import companiesApi from "@/api/companiesApi";
import usersApi from "@/api/usersApi";
import ApiError from "@/model/ApiError";
import UserProfileResponse from "@/model/UserProfileResponse";
import "@/components/CompanyUsersPanel/CompanyUsersPanel.scss";

const SEARCH_PAGE_SIZE = 20;

interface CompanyUsersPanelProps {
  companyId: string;
  users: UserProfileResponse[];
  onUsersChange: (users: UserProfileResponse[]) => void;
}

function CompanyUsersPanel({
  companyId,
  users,
  onUsersChange,
}: CompanyUsersPanelProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<UserProfileResponse[]>(
    []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState(false);

  if (!session) {
    return null;
  }

  const memberIds = new Set(
    users.map((user) => user.id).filter((id): id is string => Boolean(id))
  );

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSearching(true);
    setSearchError(false);
    setHasSearched(true);

    try {
      const result = await usersApi.listUsers(
        session.accessToken,
        0,
        SEARCH_PAGE_SIZE,
        searchTerm
      );
      setSearchResults(
        (result.content ?? []).filter(
          (user) => user.role !== "ADMIN" && !memberIds.has(user.id ?? "")
        )
      );
    } catch {
      setSearchError(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAdd = async (userId: string) => {
    setAddingUserId(userId);
    setAddError(null);

    try {
      const updatedCompany = await companiesApi.addUserToCompany(
        session.accessToken,
        companyId,
        userId
      );
      onUsersChange(updatedCompany.users ?? []);
      setSearchResults((current) =>
        current.filter((user) => user.id !== userId)
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setAddError(t("companies.usersPanel.addError"));
      } else {
        setAddError(t("companies.usersPanel.searchError"));
      }
    } finally {
      setAddingUserId(null);
    }
  };

  const handleRemove = async (userId: string) => {
    setRemovingUserId(userId);
    setRemoveError(false);

    try {
      const updatedCompany = await companiesApi.removeUserFromCompany(
        session.accessToken,
        companyId,
        userId
      );
      onUsersChange(updatedCompany.users ?? []);
    } catch {
      setRemoveError(true);
    } finally {
      setRemovingUserId(null);
    }
  };

  const closeAddPanel = () => {
    setIsAdding(false);
    setSearchTerm("");
    setSearchResults([]);
    setSearchError(false);
    setAddError(null);
    setHasSearched(false);
  };

  return (
    <div className="company-users-panel">
      <div className="company-users-panel__header">
        <h2>{t("companies.usersPanel.title")}</h2>
        <button type="button" onClick={() => (isAdding ? closeAddPanel() : setIsAdding(true))}>
          {isAdding
            ? t("companies.usersPanel.close")
            : t("companies.usersPanel.addButton")}
        </button>
      </div>

      {removeError && (
        <p className="company-users-panel__error">
          {t("companies.usersPanel.removeError")}
        </p>
      )}

      {users.length === 0 ? (
        <p>{t("companies.usersPanel.empty")}</p>
      ) : (
        <table className="company-users-panel__table">
          <thead>
            <tr>
              <th>{t("companies.usersPanel.columns.name")}</th>
              <th>{t("companies.usersPanel.columns.email")}</th>
              <th>{t("companies.usersPanel.columns.role")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={user.id ?? index}>
                <td>
                  {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                    "—"}
                </td>
                <td>{user.email ?? "—"}</td>
                <td>
                  {user.role ? t(`users.create.roles.${user.role}`) : "—"}
                </td>
                <td>
                  {user.id && (
                    <button
                      type="button"
                      onClick={() => handleRemove(user.id as string)}
                      disabled={removingUserId === user.id}
                    >
                      {removingUserId === user.id
                        ? t("companies.usersPanel.removing")
                        : t("companies.usersPanel.removeButton")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {isAdding && (
        <div className="company-users-panel__add">
          <form
            className="company-users-panel__search"
            onSubmit={handleSearch}
          >
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t("companies.usersPanel.searchPlaceholder")}
            />
            <button type="submit" disabled={isSearching}>
              {isSearching
                ? t("companies.usersPanel.searching")
                : t("companies.usersPanel.searchButton")}
            </button>
          </form>

          {addError && (
            <p className="company-users-panel__error">{addError}</p>
          )}
          {searchError && (
            <p className="company-users-panel__error">
              {t("companies.usersPanel.searchError")}
            </p>
          )}

          {!isSearching &&
            !searchError &&
            hasSearched &&
            searchResults.length === 0 && (
              <p>{t("companies.usersPanel.noResults")}</p>
            )}

          {searchResults.length > 0 && (
            <ul className="company-users-panel__results">
              {searchResults.map((user, index) => (
                <li key={user.id ?? index}>
                  <span>
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(" ") || user.email}
                    {user.email ? ` (${user.email})` : ""}
                  </span>
                  {user.id && (
                    <button
                      type="button"
                      onClick={() => handleAdd(user.id as string)}
                      disabled={addingUserId === user.id}
                    >
                      {addingUserId === user.id
                        ? t("companies.usersPanel.adding")
                        : t("companies.usersPanel.addResultButton")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default CompanyUsersPanel;
export type { CompanyUsersPanelProps };
